import { useEffect, useRef, useState, useCallback } from 'react';
import { createPortal } from 'react-dom';
import ApiDatabase from 'server';
import UseContext from 'hooks/useContext';
import useModal from 'components/modal/useModal';
import BadgeSquare from 'components/badge/badgeSquare';
import Button from 'components/button';
import DatePicker from 'react-datepicker';
import { ChevronDown, ChevronLeft, ChevronRight, Dots, Import } from 'assets/icons';

const PayslipTempWorker = ({ idUser, idBestt, initialDate }) => {
  const { toggle } = useModal();
  const {
    setModalData,
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    setAdminFileName,
    refreshPayslips,
    modalDataReturn,
  } = UseContext();

  const token = localStorage.getItem('xsrfToken');

  const [selectedDate, setSelectedDate] = useState(
    initialDate instanceof Date && !isNaN(initialDate) ? initialDate : new Date()
  );
  const [payslips, setPayslips] = useState([]);
  const [totalAmount, setTotalAmount] = useState(0);
  const [isWaiting, setIsWaiting] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);

  const year = String(selectedDate.getFullYear());
  const month = String(selectedDate.getMonth() + 1).padStart(2, '0');

  const fetchPayslips = () => {
    if (!idBestt) return;
    setIsWaiting(true);
    ApiDatabase.listPayslipsByWorker(
      { token, year, month, id_bestt: idBestt },
      (data) => {
        setPayslips(data.payslips || []);
        setTotalAmount(data.totalAmount || 0);
        setIsWaiting(false);
      },
      (err) => {
        if (err.name !== 'CanceledError') console.log(err);
        setIsWaiting(false);
      }
    );
  };

  useEffect(() => {
    fetchPayslips();
    setSelectedIds([]);
  }, [year, month, idBestt, refreshPayslips, modalDataReturn]);

  const [multiDropdownOpen, setMultiDropdownOpen] = useState(false);
  const multiDropdownRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (multiDropdownRef.current && !multiDropdownRef.current.contains(e.target))
        setMultiDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleMultipleImport = () => {
    setMultiDropdownOpen(false);
    const selected = payslips.filter(
      (p) => selectedIds.includes(p.id_temp_worker_bestt) && p.status === 'not_imported'
    );
    if (selected.length === 0) return;
    setModalData({ payslips: selected, year, month });
    toggle('payslip_import_global');
  };

  const handleMultipleDelete = () => {
    setMultiDropdownOpen(false);
    const selected = payslips.filter(
      (p) => selectedIds.includes(p.id_temp_worker_bestt) && p.status === 'imported'
    );
    if (selected.length === 0) return;
    setModalData({ payslips: selected, year, month });
    toggle('payslip_delete');
  };

  const handleExportCSV = () => {
    window.open(
      `${process.env.REACT_APP_API_URL}admin/exportPayslipsCSV?year=${year}&month=${month}&id_bestt=${idBestt}&token=${token}`,
      '_blank'
    );
  };

  const allChecked =
    payslips.length > 0 && selectedIds.length === payslips.length;

  const toggleAll = () => {
    if (allChecked) {
      setSelectedIds([]);
    } else {
      setSelectedIds(payslips.map((p) => p.id_temp_worker_bestt));
    }
  };

  return (
    <div className="border rounded p-3 flex flex-col gap-4">
      <div className="flex justify-between gap-2 items-center">
        <div className="font-medium">Fiches de paie</div>
      </div>

      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex gap-2 items-center">
          <div className="flex items-center gap-1 text-sm">
            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg border hover:bg-gray-100"
              onClick={() =>
                setSelectedDate(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() - 1, 1)
                )
              }
            >
              <ChevronLeft wh={14} color="#374151" />
            </button>
            <DatePicker
              selected={selectedDate}
              onChange={(date) => setSelectedDate(date)}
              showMonthYearPicker
              dateFormat="yyyy/MM"
              className="w-full min-w-[100px] h-8 ft-b border rounded-lg text-center text-sm"
              locale="fr-FR"
            />
            <button
              className="flex items-center justify-center w-8 h-8 rounded-lg border hover:bg-gray-100"
              onClick={() =>
                setSelectedDate(
                  (prev) => new Date(prev.getFullYear(), prev.getMonth() + 1, 1)
                )
              }
            >
              <ChevronRight wh={14} color="#374151" />
            </button>
          </div>
          <div className="border rounded py-1 px-3 text-sm font-medium bg-gray-50">
            {isWaiting ? (
              <span className="inline-block w-14 h-4 bg-gray-200 rounded animate-pulse align-middle" />
            ) : (
              `${totalAmount.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`
            )}
          </div>
        </div>

        <div className="flex gap-2 items-center">
          {selectedIds.length > 0 && (
            <div className="relative" ref={multiDropdownRef}>
              <Button
                onClick={() => setMultiDropdownOpen(!multiDropdownOpen)}
                color="PRIMARY"
                size="SMI"
              >
                <div className="flex gap-1 items-center text-sm">
                  <span>Actions ({selectedIds.length})</span>
                  <ChevronDown wh={16} color="#FFFFFF" />
                </div>
              </Button>
              {multiDropdownOpen && (
                <div className="absolute border-2 border-[#F3F4F6] rounded-lg shadow-lg w-48 top-9 right-0 z-50 bg-gray-50">
                  <div
                    className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                    onClick={handleMultipleImport}
                  >
                    <Import wh={14} color="#374151" />
                    Importer
                  </div>
                  <div className="border-b border-gray-100" />
                  <div
                    className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                    onClick={handleMultipleDelete}
                  >
                    Supprimer
                  </div>
                </div>
              )}
            </div>
          )}
          <Button size="SMI" color="SECONDARY" onClick={handleExportCSV}>
            <span className="text-sm">CSV</span>
          </Button>
        </div>
      </div>

      {isWaiting ? (
        <div className="flex flex-col gap-2">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
      ) : payslips.length === 0 ? (
        <div className="text-sm text-gray-500 text-center py-4">
          Aucune fiche de paie pour cette période
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 text-left text-gray-500">
                <th className="px-2 py-2 w-8">
                  <input
                    type="checkbox"
                    checked={allChecked}
                    onChange={toggleAll}
                    className="cursor-pointer"
                  />
                </th>
                <th className="px-2 py-2">Date</th>
                <th className="px-2 py-2">Montant</th>
                <th className="px-2 py-2">Statut</th>
                <th className="px-2 py-2 w-10" />
              </tr>
            </thead>
            <tbody>
              {payslips.map((item, idx) => (
                <PayslipWorkerRow
                  key={`${item.id_temp_worker_bestt}-${idx}`}
                  item={item}
                  year={year}
                  month={month}
                  idUser={idUser}
                  selectedIds={selectedIds}
                  setSelectedIds={setSelectedIds}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const PayslipWorkerRow = ({ item, year, month, idUser, selectedIds, setSelectedIds }) => {
  const { toggle } = useModal();
  const {
    setAdminType,
    setAdminTempWorkerId,
    setAdminUserId,
    setAdminFileName,
    setModalData,
  } = UseContext();

  const isSelected = selectedIds.includes(item.id_temp_worker_bestt);
  const isImported = item.status === 'imported';

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const dropDownRef = useRef(null);
  const dotsRef = useRef(null);

  const updateDropdownPos = useCallback(() => {
    if (dotsRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      const menuHeight = isImported ? 120 : 40;
      const fitsBelow = rect.bottom + 4 + menuHeight < window.innerHeight;
      setDropdownPos({
        top: fitsBelow ? rect.bottom + 4 : rect.top - menuHeight - 4,
        left: rect.right - 176,
      });
    }
  }, [isImported]);

  useEffect(() => {
    const handler = (e) => {
      if (
        dropDownRef.current && !dropDownRef.current.contains(e.target) &&
        dotsRef.current && !dotsRef.current.contains(e.target)
      )
        setDropdownOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleCheckbox = () => {
    setSelectedIds((prev) =>
      isSelected
        ? prev.filter((id) => id !== item.id_temp_worker_bestt)
        : [...prev, item.id_temp_worker_bestt]
    );
  };

  const handleViewPayslip = () => {
    setDropdownOpen(false);
    const fileName = `payslip_${item.id_temp_worker_bestt}_${item.year}_${item.month}`;
    setAdminType('payslip');
    setAdminTempWorkerId('');
    setAdminUserId(idUser);
    setAdminFileName(fileName);
    toggle('ad_file_view');
  };

  const handleImport = () => {
    setDropdownOpen(false);
    setModalData({ payslips: [item], year, month });
    toggle('payslip_import_unit');
  };

  const handleDelete = () => {
    setDropdownOpen(false);
    setModalData({ payslips: [item], year, month });
    toggle('payslip_delete');
  };

  return (
    <tr className="border-b border-gray-100 hover:bg-gray-50">
      <td className="px-2 py-2">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={handleCheckbox}
          className="cursor-pointer"
        />
      </td>
      <td className="px-2 py-2">
        {item.year}/{item.month}
      </td>
      <td className="px-2 py-2">
        {item.amount ? `${Number(item.amount).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €` : '-'}
      </td>
      <td className="px-2 py-2">
        <BadgeSquare type={isImported ? 12 : 13} />
      </td>
      <td className="px-2 py-2">
        <div
          className="cursor-pointer w-8 h-8 flex items-center justify-center rounded hover:bg-gray-100"
          ref={dotsRef}
          onClick={() => {
            updateDropdownPos();
            setDropdownOpen(!dropdownOpen);
          }}
        >
          <Dots w={14} h={4} color="#374151" />
        </div>
        {dropdownOpen && createPortal(
          <div
            ref={dropDownRef}
            className="fixed border-2 border-[#F3F4F6] rounded-lg shadow-lg w-44 bg-gray-50 z-[9999]"
            style={{ top: dropdownPos.top, left: dropdownPos.left }}
          >
            {!isImported ? (
              <div
                className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                onClick={handleImport}
              >
                Importer
              </div>
            ) : (
              <>
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-gray-900"
                  onClick={handleViewPayslip}
                >
                  Consulter la fiche
                </div>
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100"
                  onClick={handleImport}
                >
                  Importer à nouveau
                </div>
                <div
                  className="flex flex-row gap-2 items-center h-9 p-2 ft-sm cursor-pointer hover:bg-gray-100 text-red-500"
                  onClick={handleDelete}
                >
                  Supprimer
                </div>
              </>
            )}
          </div>,
          document.body
        )}
      </td>
    </tr>
  );
};

export default PayslipTempWorker;
